import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, MessageCircle, MapPin, BookOpen, Loader2, User as UserIcon, Star,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { VIBE_META } from '@/components/VibeQuiz';

const buildPicUrl = (pic) => {
  if (!pic) return null;
  if (pic.startsWith('http')) return pic;
  return `${process.env.REACT_APP_BACKEND_URL}${pic.startsWith('/') ? '' : '/'}${pic}`;
};

const initials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?';

const BookCard = ({ book }) => (
  <div
    data-testid={`bookshelf-book-${book.id}`}
    className="glass-card rounded-2xl p-4 flex gap-3 items-start"
  >
    {book.cover_url ? (
      <img
        src={book.cover_url}
        alt={book.title}
        className="w-14 h-20 object-cover rounded-md flex-shrink-0 shadow-md"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    ) : (
      <div className="w-14 h-20 bg-[#E2E6D5] rounded-md flex items-center justify-center flex-shrink-0">
        <BookOpen className="w-6 h-6 text-[#6B7A4F]" />
      </div>
    )}
    <div className="flex-1 min-w-0">
      <h4 className="font-semibold text-[#2D2A26] text-sm leading-tight line-clamp-2">
        {book.title}
      </h4>
      <p className="text-xs text-[#5C5955] mt-0.5 line-clamp-1">{book.author}</p>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-[10px] uppercase tracking-wider text-[#6B7A4F] font-bold">
          {book.genre}
        </span>
        {book.rating > 0 && (
          <span className="inline-flex items-center gap-0.5 text-[10px] text-[#8E9E82] font-bold">
            <Star className="w-3 h-3 fill-current" />
            {book.rating}
          </span>
        )}
      </div>
    </div>
  </div>
);

const PublicProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/profile`,
          { withCredentials: true }
        );
        if (!cancelled) setProfile(data);
      } catch (err) {
        if (!cancelled) {
          const detail = err.response?.data?.detail;
          setError(typeof detail === 'string' ? detail : 'Unable to load profile.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const vibeMeta = profile?.reader_vibe && VIBE_META ? VIBE_META[profile.reader_vibe] : null;
  const picUrl = buildPicUrl(profile?.profile_picture);

  return (
    <div className="min-h-screen bg-[#FDFBF7] paper-texture">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-10 max-w-4xl">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="mb-4 text-[#5C5955] hover:text-[#6B7A4F]"
          data-testid="public-profile-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {loading ? (
          <div className="text-center py-20">
            <Loader2 className="w-12 h-12 animate-spin text-[#6B7A4F] mx-auto" />
          </div>
        ) : error ? (
          <div className="text-center py-20 glass-card rounded-2xl" data-testid="public-profile-error">
            <UserIcon className="w-16 h-16 text-[#6B7A4F]/60 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-[#2D2A26] mb-2">Can't view this profile</h2>
            <p className="text-[#5C5955] mb-4">{error}</p>
            <Button
              onClick={() => navigate('/connections')}
              className="bg-[#6B7A4F] hover:bg-[#57653F] text-white rounded-full"
            >
              Back to Connections
            </Button>
          </div>
        ) : profile && (
          <div data-testid="public-profile-container">
            {/* Header */}
            <div className="glass-card rounded-2xl p-6 sm:p-8 mb-6">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
                <div
                  className="relative rounded-full overflow-hidden border-4 border-[#E2E6D5] shadow-lg bg-gradient-to-br from-[#E2E6D5] to-[#E8EBDD] flex items-center justify-center"
                  style={{ width: 128, height: 128 }}
                  data-testid="public-profile-avatar"
                >
                  {picUrl ? (
                    <img
                      src={picUrl}
                      alt={profile.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="font-serif font-bold text-[#6B7A4F] text-5xl">
                      {initials(profile.name)}
                    </span>
                  )}
                </div>

                <div className="flex-1 text-center sm:text-left w-full">
                  <h1
                    className="text-3xl sm:text-4xl font-light text-[#2D2A26]"
                    data-testid="public-profile-name"
                  >
                    <span className="font-bold">{profile.name}</span>
                    {profile.age != null && (
                      <span className="text-xl font-normal text-[#5C5955] ml-2">· {profile.age}</span>
                    )}
                  </h1>

                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 mt-2">
                    {profile.city && (
                      <span
                        className="inline-flex items-center gap-1 text-sm text-[#5C5955]"
                        data-testid="public-profile-city"
                      >
                        <MapPin className="w-4 h-4" />
                        {profile.city}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 text-sm text-[#5C5955]">
                      <BookOpen className="w-4 h-4" />
                      {profile.book_count} {profile.book_count === 1 ? 'book' : 'books'}
                    </span>
                  </div>

                  {vibeMeta && (
                    <div
                      className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs uppercase tracking-wider font-bold"
                      style={{
                        background: `${vibeMeta.color}20`,
                        color: vibeMeta.color,
                        border: `1px solid ${vibeMeta.color}55`,
                      }}
                      data-testid="public-profile-vibe"
                    >
                      <span className="text-base leading-none">{vibeMeta.emoji}</span>
                      {vibeMeta.name}
                    </div>
                  )}

                  <Button
                    onClick={() => navigate(`/messages?user=${profile._id}`)}
                    data-testid="public-profile-message-btn"
                    className="mt-5 bg-[#6B7A4F] hover:bg-[#57653F] text-white rounded-full px-6"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Send Message
                  </Button>
                </div>
              </div>

              {profile.bio && (
                <div className="mt-6 pt-6 border-t border-[#E1D9CD]">
                  <p
                    className="text-[#2D2A26] italic leading-relaxed"
                    data-testid="public-profile-bio"
                  >
                    "{profile.bio}"
                  </p>
                </div>
              )}
            </div>

            {/* Genres */}
            {profile.favorite_genres && profile.favorite_genres.length > 0 && (
              <div className="glass-card rounded-2xl p-6 sm:p-8 mb-6" data-testid="public-profile-genres">
                <h2 className="text-xs font-bold tracking-[0.2em] uppercase text-[#6B7A4F] mb-3">
                  Favorite Genres
                </h2>
                <div className="flex flex-wrap gap-2">
                  {profile.favorite_genres.map((g) => (
                    <span key={g} className="genre-badge bg-[#E2E6D5] text-[#6B7A4F]">{g}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Reading preferences */}
            {profile.reading_preferences && (
              <div className="glass-card rounded-2xl p-6 sm:p-8 mb-6">
                <h2 className="text-xs font-bold tracking-[0.2em] uppercase text-[#6B7A4F] mb-3">
                  Reading Preferences
                </h2>
                <p
                  className="text-[#2D2A26] leading-relaxed"
                  data-testid="public-profile-preferences"
                >
                  {profile.reading_preferences}
                </p>
              </div>
            )}

            {/* Bookshelf */}
            <div className="glass-card rounded-2xl p-6 sm:p-8" data-testid="public-profile-bookshelf">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xs font-bold tracking-[0.2em] uppercase text-[#6B7A4F]">
                  Bookshelf
                </h2>
                <span className="text-sm text-[#5C5955]">
                  {profile.book_count} {profile.book_count === 1 ? 'title' : 'titles'}
                </span>
              </div>
              {profile.books && profile.books.length > 0 ? (
                <div className="grid sm:grid-cols-2 gap-4">
                  {profile.books.map((b) => (
                    <BookCard key={b.id} book={b} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-[#5C5955] text-sm">
                  <BookOpen className="w-10 h-10 text-[#6B7A4F]/40 mx-auto mb-2" />
                  {profile.name} hasn't added any books yet.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicProfile;
